<template>
  <CardSection :error="majorError" :loading="isSavableStateLoading">
    <template #header>
      Manage Super Team Dashboards
    </template>
    <template #body>
      <div class="form-group row">
        <div class="form-group mr-2 mb-0 col-12 d-flex">
          <router-link :to="{ name: 'admin_super-team-dashboards_new' }"
            class="btn btn-primary btn-sm ml-auto">
            Add New Configuration
          </router-link>
        </div>
      </div>
      <PortalTable ref="super_team_dashboard_index" table-id="super_team_dashboard_index"
        :table-data="records"
        :total-records="totalRecords"
        :page="pageNumber"
        :page-count="pageCount"
        :loading="isSavableStateProcessing"
        :filter-values="filterValues"
        filter-display="row"
        empty="No Super team dashboard configurations found!"
        @on-sort="handleSortChange"
        @on-page="handlePageChange"
        @on-filter-change="onFilterChange">
        <template #columns>
          <Column field="name"
            header="Name"
            filter-field="name"
            sortable
            :show-filter-menu="false">
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <InputText v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search by name"
                @input="filterCallback()" />
            </template>
          </Column>
          <Column field="txcOrganGroupList" 
            header="TxC - Organ Groups" 
            :show-filter-menu="false"
            filter-field="txc_organ_id"
            :filter-menu-style="{ width: '15rem' }">
            <template #filter>
              <MultiSelectFilter ref="txc_organ_id" 
                filter-field="txc_organ_id"
                class="form-control form-control-select"
                :options="txcOrganGroupOptionsList"
                option-label="label"
                @change="onChange" />
            </template>
            <template #body="slotProps">
              <ul>
                <li v-for="t in slotProps.data.txcOrganGroupList" :key="t">{{ t }}</li>
              </ul>
            </template>
          </Column>
          <Column field="status" 
            header="Statuses" 
            :show-filter-menu="false"
            :filter-menu-style="{ width: '15rem' }">
            <template #body="slotProps">
              <ul>
                <li v-for="t in slotProps.data.statusNames" :key="t">{{ t }}</li>
              </ul>
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <router-link :to="{ name: 'admin_super-team-dashboards_edit', params: { id: slotProps.data.id } }"
                class="action btn btn-fill btn-outline-primary mx-2">
                Edit
              </router-link>
            </template>
          </Column>
        </template>
      </PortalTable>
    </template>
  </CardSection>
</template>

<script>
import CardSection from "@/src/components/shared/CardSection.vue";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import { pageMixin } from "@/src/mixins/pageMixin";
import { FilterMatchMode } from '@primevue/core/api';
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";
import { useSuperTeamDashboardStore } from '@/src/stores/dashboards/super-team-dashboards';
import { paginationMixin } from "@/src/mixins/paginationMixin";
import InputText from 'primevue/inputtext';

export default {
   components: {
    CardSection,
    PortalTable,
    Column,
    MultiSelectFilter,
    InputText
  },
  mixins: [ savableStateMixin, pageMixin, paginationMixin ],
  setup() {
    const superTeamDashboardStore = useSuperTeamDashboardStore();
    return {
      superTeamDashboardStore
    }
  },
   data() {
    return {
      majorError: null,
      transplantCenters: null,
      filterValues: {
        "txc_organ_id" : { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "name": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      }
    }
  },
  async mounted() {
    try {
      this.setSavableStateLoading();
      await Promise.all([
        this.superTeamDashboardStore.loadAllTransplantCentres(),
        this.superTeamDashboardStore.loadAllOrganGroups(),
        this.superTeamDashboardStore.loadAllStatuses()
      ]);
      this.setSavableStateIdle();
    } catch(error) {
      this.handleMajorError(error); 
    }
    await this.getRowData();
  },
  methods: {
    async getRowData(reset = false) {
      this.setSavableStateProcessing();

      if (reset) this.pageNumber = 1;

      let params = {
        page: this.pageNumber,
        sort_by: this.currentSortField,
        sort_type: this.currentSortType,
      };

      const txc_organs_filters = [];

      if(this.search_params && this.search_params.txc_organ_id) {
        this.search_params.txc_organ_id.forEach(i => {
          const i_og = this.txcOrganGroupOptionsList.find(x => x.id === i);
          if(!i_og) return;

          txc_organs_filters.push({
            transplant_center_id: i_og.transplant_center_id,
            organ_group_id: i_og.organ_group_id
          });
        })
      }
      
      params = { ...this.search_params, ...params, txc_organs: txc_organs_filters };

      try {
        const response = await this.superTeamDashboardStore.loadSuperTeamDashboardConfigs(params)
        this.processPagyResponse(response, this.processIncomingData);
        this.setSavableStateIdle();
      } catch(error) {
        this.handleMajorError(error);
      }
    },
    onChange(event) {
      this.$refs.super_team_dashboard_index.resetFilters(event);
    },
    onFilterChange(event) {
      if(event["transplant_center_id"] === "") this.$refs.transplant_center_id.clearFilters();
      if(event["status_id"] === "") this.$refs.status_id.clearFilters();

      this.search_params = event;
      this.getRowData(true);
    },
    /**
     * Processes the incoming data from the API response to transform it into a user-friendly format.
     * This method formats the transplant center and organ group data into a readable list,
     * and translates status IDs into their corresponding names.
     *
     * @param {Array} responseData - The raw data from the API response.
     * @returns {Array} - The processed data with formatted transplant center-organ group pairs and status names.
     */
    processIncomingData(responseData) {
      responseData.forEach((item) => {
        if(!item.config) return;

        const txcOrganGroupList = [];
        const statusNames = [];

        (item.config.txc_organs || []).forEach(txcOrgan => {
          const txc = this.superTeamDashboardStore.allTransplantCenters.find((t) => t.id === txcOrgan.transplant_center_id);
          const og = this.superTeamDashboardStore.allOrganGroups.find((o) => o.id === txcOrgan.organ_group_id);
          txcOrganGroupList.push(`${txc.code} - ${og.name}`);
        });

        (item.config.status_ids || []).forEach(s_id => {
          const s = this.superTeamDashboardStore.allStatuses.find(s => s.id === s_id);
          statusNames.push(s.name);
        })

        item.txcOrganGroupList = txcOrganGroupList;
        item.statusNames = statusNames;
      })

      return responseData;
    }
  },
  computed: {
    txcOrganGroupOptionsList() {
      const list = [];
      if(!this.superTeamDashboardStore.allTransplantCenters || !this.superTeamDashboardStore.allOrganGroups) return list;

      this.superTeamDashboardStore.allTransplantCenters.forEach((t) => {
        this.superTeamDashboardStore.allOrganGroups.forEach((og) => {
          list.push({
            label: `${t.code} - ${og.name}`,
            id: `${t.id} - ${og.id}`,
            transplant_center_id: t.id, 
            organ_group_id: og.id
          });
        });
      });
      return list;
    }
  }
}
</script>